import React from "react";
import {
  BarChart3, Users, Clock, Zap, Target, Globe, AppWindow, TrendingUp, Layers,
  Sparkles, AlertCircle, Code2, CheckCircle2, Bug, Lightbulb, ThumbsUp, Square,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  AreaChart, Area, PieChart, Pie,
} from "recharts";
import { motion } from "framer-motion";
import { useAuth } from "../../../contexts/AuthContext";
import { useDashTheme } from "../hooks/useDashTheme";
import { useAiStream } from "../hooks/useAiStream";
import { PageShell } from "../components/PageShell";
import { SectionCard, Badge, Avatar, Button } from "../components/primitives";
import { EmptyState } from "../components/DataTable";
import { HowItWorks } from "../components/HowItWorks";
import { DateRangeTool, presetToRange } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import AILimitModal from "../../../components/AILimitModal/AILimitModal";
import { SERVER_URL } from "../../../utils/serverConfig";

const fmt = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const HIGHLIGHT_ICONS = { code: Code2, check: CheckCircle2, bug: Bug, tip: Lightbulb };

// ─────────────────────────────────────────────────────────────────────────────
// Right-rail pieces (compact analytics)
// ─────────────────────────────────────────────────────────────────────────────

function Kpi({ label, value, suffix, icon, accent }) {
  const t = useDashTheme();
  return (
    <div style={{
      padding: `${t.s[3]}px ${t.s[3]}px`, borderRadius: t.r.md,
      border: `1px solid ${t.c.border}`, background: t.c.surface,
      display: "flex", flexDirection: "column", gap: 3, minWidth: 0,
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: t.c.textTertiary, fontWeight: 500, whiteSpace: "nowrap" }}>
        <span style={{ display: "inline-flex", color: accent || t.c.icon }}>{icon}</span>{label}
      </span>
      <span style={{ fontSize: 19, fontWeight: 700, color: t.c.text, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
        {value ?? "—"}<span style={{ fontSize: t.font.size.xs, fontWeight: 500, color: t.c.textTertiary, marginLeft: 2 }}>{value != null ? suffix : ""}</span>
      </span>
    </div>
  );
}

function ChartTip({ active, payload, label, suffix = "" }) {
  const t = useDashTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg, padding: "6px 10px", fontSize: t.font.size.xs }}>
      <div style={{ color: t.c.textTertiary, marginBottom: 2 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: t.c.text, fontWeight: 600 }}>{p.name || p.dataKey}: {p.value}{suffix}</div>
      ))}
    </div>
  );
}

function RankedBars({ items, color, withAvatar, empty = "Not enough evidence in this range." }) {
  const t = useDashTheme();
  if (!items?.length) {
    return <div style={{ padding: t.s[4], fontSize: t.font.size.xs, color: t.c.textTertiary }}>{empty}</div>;
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[2], padding: t.s[3] }}>
      {items.map((it) => (
        <div key={it.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {withAvatar && <Avatar name={it.name} size={18} />}
          <span style={{ width: 92, fontSize: t.font.size.xs, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{it.name}</span>
          <div style={{ flex: 1, height: 6, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden" }}>
            <div style={{ width: `${Math.round((it.count / max) * 100)}%`, height: "100%", borderRadius: 99, background: color, transition: "width 500ms ease" }} />
          </div>
          <span style={{ width: 34, textAlign: "right", fontSize: 10.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>{it.count}</span>
        </div>
      ))}
    </div>
  );
}

// Small titled block for the right rail — lighter than SectionCard.
function RailBlock({ title, icon, children }) {
  const t = useDashTheme();
  return (
    <div style={{ borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, padding: `${t.s[3]}px ${t.s[3]}px ${t.s[2]}px` }}>
        <span style={{ display: "inline-flex", color: t.c.icon }}>{icon}</span>
        <span style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textSecondary, letterSpacing: "0.01em" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Left-column pieces (the precise work analysis)
// ─────────────────────────────────────────────────────────────────────────────

function HighlightRow({ icon, title, text }) {
  const t = useDashTheme();
  const Icon = HIGHLIGHT_ICONS[icon] || Sparkles;
  return (
    <div style={{ display: "flex", gap: 12, padding: `${t.s[3]}px 0` }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30,
        borderRadius: t.r.md, background: t.c.primary + "14", color: t.c.primary, flexShrink: 0, marginTop: 1,
      }}><Icon size={15} /></span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: t.font.size.md, fontWeight: 650, color: t.c.text, letterSpacing: "-0.01em" }}>{title}</div>
        <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.55, marginTop: 2 }}>{text}</div>
      </div>
    </div>
  );
}

function SessionBlock({ s }) {
  const t = useDashTheme();
  const intensityTone = s.intensity === "high" ? "positive" : s.intensity === "low" ? "warning" : "neutral";
  return (
    <div style={{ borderRadius: t.r.lg, border: `1px solid ${t.c.border}`, background: t.c.surface, padding: t.s[4] }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>{s.timeRange}</span>
        <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>·</span>
        <span style={{ fontSize: t.font.size.xs, fontWeight: 600, color: t.c.textSecondary }}>{s.duration}</span>
        {s.project && <Badge tone="neutral">{s.project}</Badge>}
        {s.intensity && <span style={{ marginLeft: "auto" }}><Badge tone={intensityTone}>{s.intensity} intensity</Badge></span>}
      </div>
      <div style={{ fontSize: t.font.size.lg, fontWeight: 650, color: t.c.text, letterSpacing: "-0.01em", lineHeight: 1.3 }}>{s.title}</div>
      {s.summary && <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.6, margin: `${t.s[2]}px 0 0` }}>{s.summary}</p>}
      {s.activities?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: t.s[3] }}>
          {s.activities.map((a) => (
            <span key={a.label} title={a.detail} style={{
              fontSize: t.font.size.xs, padding: "3px 9px", borderRadius: 99,
              background: t.c.surfaceMuted, color: t.c.textSecondary, border: `1px solid ${t.c.border}`,
            }}>{a.label} <span style={{ color: t.c.textTertiary }}>· {a.weight}%</span></span>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightRow({ type, title, text }) {
  const t = useDashTheme();
  const Icon = type === "positive" ? ThumbsUp : type === "warning" ? AlertCircle : Lightbulb;
  const color = type === "positive" ? t.status.positive : type === "warning" ? t.status.warning : t.c.primary;
  return (
    <div style={{ display: "flex", gap: 12, padding: `${t.s[3]}px 0` }}>
      <span style={{ display: "inline-flex", color, flexShrink: 0, marginTop: 2 }}><Icon size={15} /></span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: t.font.size.sm, fontWeight: 650, color: t.c.text }}>{title}</div>
        <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: 1.55, marginTop: 1 }}>{text}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function WorkIntelligenceV2() {
  const t = useDashTheme();
  const { user } = useAuth();
  // Same generator + cache the Work Summary page uses (Claude Sonnet); cached
  // reports return instantly and cost nothing.
  const stream = useAiStream("/api/v1/work-summary/metrics-stream");

  const [member, setMember] = React.useState(null);
  const [range, setRange] = React.useState(() => presetToRange("yesterday"));
  const [accessibleUsers, setAccessibleUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/saved-summaries?limit=1`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (json?.success && json.data?.accessibleUsers) {
          setAccessibleUsers(json.data.accessibleUsers);
          const me = json.data.accessibleUsers.find((u) => u._id === user?._id) || json.data.accessibleUsers[0];
          if (me) setMember((cur) => cur || me._id);
        }
      } catch (_) {} finally { setLoadingUsers(false); }
    })();
  }, [user]);

  const members = React.useMemo(() => accessibleUsers.map((u) => ({
    value: u._id || u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
    sub: u.position || u.email,
  })), [accessibleUsers]);

  // Right rail = Mongo query only. Auto-load on member/range — never wait for AI.
  React.useEffect(() => {
    if (!member) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const token = localStorage.getItem("token");
        const qs = new URLSearchParams({ userId: member, startDate: fmt(range.start), endDate: fmt(range.end) });
        const res = await fetch(`${SERVER_URL}/api/v1/work-intelligence/insights?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (cancelled) return;
        if (json.success) setData(json.data);
        else setError(json.message || "Failed to load analytics");
      } catch (e) {
        if (!cancelled) setError("Couldn't reach the server. Is the backend running?");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [member, range]);

  // Analyze = left column AI only. Right rail is already loaded.
  const analyze = React.useCallback(() => {
    if (!member) return;
    stream.run({ userId: member, startDate: fmt(range.start), endDate: fmt(range.end), forceRegenerate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, range]);

  const onChangeMember = (v) => { setMember(v || null); setData(null); setError(null); stream.reset?.(); };
  const onChangeRange = (r) => { setRange(r); setData(null); setError(null); stream.reset?.(); };

  const k = data?.kpis;
  const report = stream.report;
  // Show the split layout as soon as the (fast) analytics query returns.
  const started = !!data || !!error || loading;
  const axisTick = { fontSize: 9.5, fill: t.c.textTertiary };
  const donutColors = t.accents || [t.c.primary];
  const sentimentTone = report?.overallSentiment === "positive" ? "positive"
    : report?.overallSentiment === "negative" ? "warning" : "neutral";
  const dateSubtitle = `${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  return (
    <PageShell
      eyebrow="AI · Analytics"
      title="Work Intelligence"
      subtitle="Precise, evidence-based breakdown of a member's work"
      icon={<BarChart3 size={22} />}
      topRight={
        <div className="tg-filterbar">
          <ToolSelect
            icon={<Users size={13} />}
            value={member}
            options={members}
            onChange={onChangeMember}
            placeholder={loadingUsers ? "Loading users…" : `Select a member (${members.length} accessible)`}
            panelWidth={260}
            placement="bottomRight"
          />
          <span className="tg-tool-sep" />
          <DateRangeTool range={range} onChange={onChangeRange} layoutId="workIntelRangeChip"
            presets={[
              { key: "yesterday", label: "Yesterday" },
              { key: "7d", label: "7 days" },
              { key: "30d", label: "30 days" },
              { key: "month", label: "This month" },
            ]}
          />
        </div>
      }
      actions={
        stream.streaming ? (
          <Button variant="secondary" leftIcon={<Square size={14} />} onClick={stream.stop}>Stop</Button>
        ) : (
          <Button variant="primary" leftIcon={<Zap size={15} />} onClick={analyze} disabled={!member || stream.streaming}>
            Analyze
          </Button>
        )
      }
    >
      {!started ? (
        <HowItWorks
          icon={<BarChart3 size={18} />}
          steps={[
            "Select a team member and a date range",
            "Click “Analyze” — cached reports load instantly, new ones stream in",
            "Read the work analysis on the left, scan the analytics rail on the right",
          ]}
          bullets={[
            "Session-by-session breakdown with real file, commit and task evidence",
            "Key highlights and actionable insights",
            "Tracked hours, rhythm, work-type mix and collaboration analytics",
          ]}
          tip="Analytics are computed from tracked evidence — deterministic and free to re-run."
        />
      ) : (
        <>
          {/* ── Top: headline analysis banner ── */}
          {(report?.headline || stream.streaming) && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 12, marginBottom: t.s[4],
              padding: `${t.s[4]}px ${t.s[5]}px`, borderRadius: t.r.lg,
              border: `1px solid ${t.c.border}`, background: t.c.surface,
            }}>
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34,
                borderRadius: t.r.md, background: t.c.primaryGradient, color: "#fff", flexShrink: 0,
              }}>
                {stream.streaming
                  ? <motion.span animate={{ rotate: 360 }} transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }} style={{ display: "inline-flex" }}><Sparkles size={16} /></motion.span>
                  : <Sparkles size={16} />}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: t.font.size.lg, fontWeight: 650, color: t.c.text, letterSpacing: "-0.01em", lineHeight: 1.35 }}>
                  {report?.headline || "Analyzing tracked evidence…"}
                </div>
                {report?.focusHeadline && (
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 2 }}>{report.focusHeadline} · {dateSubtitle}</div>
                )}
              </div>
              {report?.overallSentiment && <Badge tone={sentimentTone}>{report.overallSentiment}</Badge>}
            </div>
          )}

          {/* ── 70 / 30 split ── */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 7fr) minmax(290px, 3fr)", gap: t.s[4], alignItems: "start" }}>

            {/* LEFT · the work analysis */}
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[4], minWidth: 0 }}>
              {stream.error && !report ? (
                <EmptyState
                  icon={<AlertCircle size={22} />}
                  title={/no tracked activity/i.test(stream.error || "") ? "No activity found" : "Analysis unavailable"}
                  description={(stream.error || "").replace(/\*\*/g, "")}
                  action={!/no tracked activity/i.test(stream.error || "")
                    ? <Button variant="primary" leftIcon={<Zap size={15} />} onClick={analyze}>Retry</Button>
                    : null}
                />
              ) : !report && stream.streaming ? (
                <SectionCard title="Work analysis" icon={<Sparkles size={15} />}>
                  <div style={{ padding: t.s[6], display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                    <motion.span animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 1.2, repeat: Infinity }}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 14, background: t.c.primaryGradient, color: "#fff" }}>
                      <Sparkles size={20} />
                    </motion.span>
                    <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text }}>Reading the evidence…</div>
                    <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>Building a precise, session-level analysis</div>
                  </div>
                </SectionCard>
              ) : report ? (
                <>
                  {report.highlights?.length > 0 && (
                    <SectionCard title="Key highlights" subtitle="The work that mattered most in this range" icon={<CheckCircle2 size={15} />}>
                      <div style={{ padding: `${t.s[1]}px ${t.s[4]}px` }}>
                        {report.highlights.map((h, i) => <HighlightRow key={i} {...h} />)}
                      </div>
                    </SectionCard>
                  )}
                  {report.sessions?.length > 0 && (
                    <SectionCard title="Session breakdown" subtitle="What happened, block by block" icon={<Clock size={15} />}>
                      <div style={{ padding: t.s[4], display: "flex", flexDirection: "column", gap: t.s[3] }}>
                        {report.sessions.map((s, i) => <SessionBlock key={i} s={s} />)}
                      </div>
                    </SectionCard>
                  )}
                  {report.insights?.length > 0 && (
                    <SectionCard title="Insights & recommendations" icon={<Lightbulb size={15} />}>
                      <div style={{ padding: `${t.s[1]}px ${t.s[4]}px` }}>
                        {report.insights.map((x, i) => <InsightRow key={i} {...x} />)}
                      </div>
                    </SectionCard>
                  )}
                </>
              ) : (
                <SectionCard title="Work analysis" icon={<Sparkles size={15} />}>
                  <div style={{ padding: t.s[5], fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                    No analysis available for this range yet. Click Analyze to generate one.
                  </div>
                </SectionCard>
              )}
            </div>

            {/* RIGHT · analytics rail (~30%) */}
            <div style={{ display: "flex", flexDirection: "column", gap: t.s[3], position: "sticky", top: 0 }}>
              {error ? (
                <RailBlock title="Analytics" icon={<AlertCircle size={12} />}>
                  <div style={{ padding: t.s[3], fontSize: t.font.size.xs, color: t.c.textTertiary }}>{error}</div>
                </RailBlock>
              ) : loading ? (
                <RailBlock title="Analytics" icon={<BarChart3 size={12} />}>
                  <div style={{ padding: t.s[4], display: "flex", flexDirection: "column", gap: 8 }}>
                    {[1, 2, 3].map((i) => <div key={i} style={{ height: 40, borderRadius: t.r.md, background: t.c.surfaceMuted }} className="animate-pulse" />)}
                  </div>
                </RailBlock>
              ) : data ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.s[2] }}>
                    <Kpi label="Tracked hours" value={k?.totalHours} suffix="h" icon={<Clock size={11} />} accent={t.accents?.[0]} />
                    <Kpi label="Sessions" value={k?.sessions} icon={<Layers size={11} />} accent={t.accents?.[1]} />
                    <Kpi label="Active days" value={k?.activeDays} icon={<TrendingUp size={11} />} accent={t.accents?.[2]} />
                    <Kpi label="Focus" value={k?.focusRatio != null ? Math.round(k.focusRatio * 100) : null} suffix="%" icon={<Target size={11} />} accent={t.accents?.[4]} />
                  </div>

                  <RailBlock title="Daily rhythm · by hour (IST)" icon={<Clock size={12} />}>
                    <div style={{ padding: `0 ${t.s[2]}px ${t.s[2]}px` }}>
                      <ResponsiveContainer width="100%" height={110}>
                        <BarChart data={data.hourly || []}>
                          <XAxis dataKey="hour" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(h) => `${h}`} interval={1} />
                          <YAxis hide />
                          <Tooltip content={<ChartTip />} cursor={{ fill: t.c.surfaceMuted }} />
                          <Bar dataKey="count" name="activity" radius={[3, 3, 0, 0]} fill={t.c.primary} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </RailBlock>

                  {(data.dailyTrend?.length || 0) > 1 && (
                    <RailBlock title="Activity trend" icon={<TrendingUp size={12} />}>
                      <div style={{ padding: `0 ${t.s[2]}px ${t.s[2]}px` }}>
                        <ResponsiveContainer width="100%" height={100}>
                          <AreaChart data={data.dailyTrend}>
                            <XAxis dataKey="date" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} />
                            <YAxis hide />
                            <Tooltip content={<ChartTip />} />
                            <Area dataKey="screenshots" name="activity" stroke={t.c.primary} fill={t.c.primary + "33"} strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </RailBlock>
                  )}

                  <RailBlock title="Work type mix" icon={<Layers size={12} />}>
                    <RankedBars items={data.workTypes} color={t.accents?.[1] || t.c.primary} />
                  </RailBlock>

                  <RailBlock title="Collaboration circle" icon={<Users size={12} />}>
                    <RankedBars items={data.collaborators} color={t.accents?.[4] || t.c.primary} withAvatar />
                  </RailBlock>

                  {(data.projects?.length || 0) > 0 && (
                    <RailBlock title="Project split" icon={<Target size={12} />}>
                      <div style={{ display: "flex", alignItems: "center", padding: `0 ${t.s[3]}px ${t.s[3]}px` }}>
                        <ResponsiveContainer width="45%" height={110}>
                          <PieChart>
                            <Pie data={data.projects} dataKey="hours" nameKey="name" innerRadius={28} outerRadius={46} paddingAngle={3} strokeWidth={0}>
                              {data.projects.map((_, i) => <Cell key={i} fill={donutColors[i % donutColors.length]} />)}
                            </Pie>
                            <Tooltip content={<ChartTip suffix="h" />} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
                          {data.projects.slice(0, 5).map((p, i) => (
                            <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5 }}>
                              <span style={{ width: 7, height: 7, borderRadius: 99, background: donutColors[i % donutColors.length], flexShrink: 0 }} />
                              <span style={{ color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                              <span style={{ marginLeft: "auto", color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>{p.hours}h</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </RailBlock>
                  )}

                  {((data.apps?.length || 0) > 0 || (data.domains?.length || 0) > 0) && (
                    <RailBlock title="Tools & surfaces" icon={<AppWindow size={12} />}>
                      <div style={{ padding: `0 ${t.s[3]}px ${t.s[3]}px`, display: "flex", flexDirection: "column", gap: t.s[2] }}>
                        {[["Apps", data.apps, AppWindow], ["Websites", data.domains, Globe]].map(([label, list, Icon]) => (list || []).length > 0 && (
                          <div key={label}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: t.c.textTertiary, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <Icon size={10} />{label}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                              {list.slice(0, 8).map((x) => (
                                <span key={x.name} style={{
                                  fontSize: 10, padding: "2px 7px", borderRadius: 99,
                                  background: t.c.surfaceMuted, color: t.c.textSecondary, border: `1px solid ${t.c.border}`,
                                }}>{x.name}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </RailBlock>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}

      <AILimitModal isOpen={stream.limitReached} onClose={() => stream.setLimitReached(false)} />
    </PageShell>
  );
}
